import { useCallback, useEffect, useMemo, useState } from "react";

import { AiProcessingContent } from "components/modals/Files-categorization-modal/AiProcessedContent";
import { useActionHook, useModals, useNavigationHooks, usePrompt, useUrlHooks } from "hooks";
import { isNull } from "lodash";
import { IApiServiceError, useDealActions, useUserRoleSelector } from "store";
import {
  IApproveMeasureFilters,
  IRecommendationChild,
  IRecommendationData,
  IRecommendationItem,
  IUpdateRecommendationStatusPayload,
  PagedRecommendations,
  RecommendationStatus
} from "store/types";

import { RoleName } from "../../../constants";
import { defaultFileFilters } from "../AiDataFilter/FiltersDrawer";
import {
  AiDataFilters,
  IChangeSpecificRecommendationValue,
  IHandleCellClick,
  IOpenedCell,
  RecommendationField
} from "./AiData-context";

// Helper function to generate unique identifier from existing fields
const generateUniqueId = (item: IRecommendationChild | IRecommendationData, fileId: number): string => {
  // For IRecommendationData (root level)
  if ("file" in item && "deal" in item) {
    return `file-${fileId}-deal-${item?.deal?.id}`;
  }

  // For IRecommendationItem (has id field)
  if ("id" in item) {
    // Use id as primary identifier, with fallbacks for uniqueness
    return `file-${fileId}-item-${item.id}-polygon-${item.polygon_id}-measure-${item.measure?.id}-investor-${item.investor?.id}-date-${item.date}`;
  }

  // For IMeasureGroupOneInvestor (has investor and measure)
  if ("investor" in item && "measure" in item && !("id" in item)) {
    // Include date to make it more unique
    return `file-${fileId}-investor-${item.investor?.id}-measure-${item.measure?.id}-date-${item.date}`;
  }

  // For IMeasureGroupChild (has measure only)
  if ("measure" in item && !("investor" in item) && !("id" in item)) {
    return `file-${fileId}-measure-${item.measure?.id}`;
  }

  // Fallback - should not happen
  return crypto.randomUUID();
};

interface Params {
  defaultPerPage?: number;
}

export const useAiDataContextHook = (params?: Params) => {
  const { defaultPerPage = 50 } = params || {};
  const role = useUserRoleSelector();
  const { queryParams } = useUrlHooks();
  const { removeParams } = useNavigationHooks();
  const { createdAt, dealId } = queryParams;
  const {
    getRecommendationsTableAction,
    updateRecommendationStatusAction,
    updateRecommendationAction,
    regenerateRecommendationDataAction
  } = useDealActions();
  const { errorModal } = useModals();

  const userHasEditPermission = useMemo(() => {
    return role?.name === RoleName.Admin || role?.name === RoleName.Analyst;
  }, [role]);

  const { loading: isRecommendationsTableLoading, action: getTable } = useActionHook(getRecommendationsTableAction, {
    onSuccess: (data: PagedRecommendations) => {
      setRecommendationsTable(data.recommendations);
      setPagination({
        current_page: data.current_page,
        per_page: data.per_page,
        total: data.total,
        last_page: data.last_page
      });
    }
  });

  const [recommendationsTable, setRecommendationsTable] = useState<IRecommendationData[]>([]);
  const [pagination, setPagination] = useState<{
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  } | null>(null);

  const openPrompt = usePrompt();

  const [openedCell, setOpenedCell] = useState<IOpenedCell | null>(null);
  const [filters, setFilters] = useState<AiDataFilters>(defaultFileFilters);

  // Unified state for all row expansions (file, measure group, parameter)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Checkbox selection state
  const [selectedUiIds, setSelectedUiIds] = useState<Set<string>>(new Set());
  const [selectedParameterIds, setSelectedParameterIds] = useState<number[]>([]);

  const updateRecommendationsTable = useCallback(
    (data: { [key: string]: IRecommendationData }) => {
      if (!recommendationsTable) return recommendationsTable;

      const updatedTable = recommendationsTable.map((row) => {
        const updatedRecord = Object.entries(data).find(([key]) => Number(key) === row.file.id);
        if (updatedRecord) {
          return updatedRecord[1];
        }
        return row;
      });
      return updatedTable;
    },
    [recommendationsTable]
  );

  const handleCellClick = useCallback(({ event, data, field }: IHandleCellClick) => {
    clearAllSelections();
    if (data.parameter.status !== RecommendationStatus.new) return;
    event.stopPropagation();
    const target = event.currentTarget;
    //if parameter measure is not set, show error message
    if (!data.parameter.measure && field !== RecommendationField.measure) {
      errorModal({ msg: "Add a parameter to enable editing of data." });
      return;
    }
    setOpenedCell({
      anchorEl: target,
      data,
      selectedField: field
    });
  }, []);

  //---------------approve recommendation  ---------

  const onGroupRowApprove = useCallback(
    async (payload: IApproveMeasureFilters) => {
      const approvePayload: IUpdateRecommendationStatusPayload = {
        accepted_ids: [],
        declined_ids: [],
        new_ids: [],
        filters: {
          file_id: payload.file_id,
          measure_id: payload.measure_id,
          ...(payload.investor_id && { investor_id: payload.investor_id }),
          ...(payload.date && { date: payload.date })
        }
      };
      const data = await updateRecommendationStatusAction(approvePayload);
      if (!data || !recommendationsTable) return;
      const updatedTable = updateRecommendationsTable(data);
      setRecommendationsTable(updatedTable);
    },
    [recommendationsTable, updateRecommendationStatusAction, updateRecommendationsTable]
  );

  //---------------patch recommendation  ---------

  const onChangeSpecificRecommendationValue = useCallback(
    async (data: IChangeSpecificRecommendationValue) => {
      if (!recommendationsTable) return;
      const payload =
        typeof data.value === "object"
          ? {
              [`${data.field}_id`]: data.value.id,
              ...(data.value?.split_by_commitments && { split_by_commitments: data.value?.split_by_commitments })
            }
          : {
              [data.field]: data.value
            };
      const res = await updateRecommendationAction({
        id: data.id,
        payload
      });
      if (res) {
        const fileId = res?.file?.id;
        const updatedTable = recommendationsTable?.map((row) => (row.file.id === fileId ? res : row));

        setRecommendationsTable(updatedTable);
      }
    },
    [recommendationsTable, setRecommendationsTable, updateRecommendationsTable]
  );

  const onChangeRecommendationStatus = useCallback(
    async (status: RecommendationStatus, id: number) => {
      try {
        const payload = {
          accepted_ids: status === RecommendationStatus.accepted ? [id] : [],
          declined_ids: status === RecommendationStatus.declined ? [id] : [],
          new_ids: status === RecommendationStatus.new ? [id] : []
        };

        const data = await updateRecommendationStatusAction(payload);
        if (!data || !recommendationsTable) return;

        const updatedTable = updateRecommendationsTable(data);
        setRecommendationsTable(updatedTable);
        clearAllSelections();
      } catch (err) {
        errorModal({ msg: Object.values((err as IApiServiceError).errors)[0][0] || "Something went wrong" });
      }
    },
    [updateRecommendationStatusAction, recommendationsTable, setRecommendationsTable]
  );

  const regenerateRecommendationData = useCallback(
    async (fileId: number) => {
      clearAllSelections();
      try {
        await regenerateRecommendationDataAction(fileId);
        openPrompt({
          Component: AiProcessingContent,
          props: {
            title: "Regenerate recommendation",
            type: "aiExtraction",
            applyButtonText: "Continue"
          }
        });
        const updatedTable = recommendationsTable?.filter((row) => row.file.id !== fileId);
        setRecommendationsTable(updatedTable);
      } catch {
        errorModal({ msg: "Something went wrong" });
      }
    },
    [regenerateRecommendationDataAction, recommendationsTable, setRecommendationsTable]
  );

  // Helper function to check if all children are accepted
  const allChildAccepted = (item: IRecommendationChild): boolean => {
    if (item.children && item.children.length > 0) {
      return item.children.every((child: IRecommendationChild) => allChildAccepted(child));
    }

    // check only if it has status
    if ("status" in item) {
      return item.status === RecommendationStatus.accepted;
    }

    // If no children and no status
    return true;
  };

  // Helper function to check if a recommendation is filled
  const isRecommendationFilled = (recommendation: IRecommendationItem): boolean => {
    return Boolean(
      recommendation.measure?.id &&
        typeof recommendation.value === "number" &&
        //if measure is not global value, then investor is required
        (recommendation.measure?.is_global_value ? true : recommendation.investor?.id) &&
        recommendation.date
    );
  };

  // Helper function to get missing required fields for a recommendation
  const getMissingRequiredFields = (recommendation: IRecommendationItem): string[] => {
    const missingFields: string[] = [];

    if (!recommendation.measure?.id) {
      missingFields.push("Parameter");
    }

    if (typeof recommendation.value !== "number") {
      missingFields.push("Value");
    }

    // Investor is required only if measure is not global value
    if (!recommendation.measure?.is_global_value && !recommendation.investor?.id) {
      missingFields.push("Investor");
    }

    if (!recommendation.date) {
      missingFields.push("Date");
    }

    return missingFields;
  };

  // Helper function to recursively find a parameter by ID in the hierarchy
  const findParameterById = (
    item: IRecommendationChild | IRecommendationData,
    targetId: number
  ): IRecommendationItem | null => {
    // If this item itself is a parameter with the target ID
    if ("id" in item && item.id === targetId) {
      return item as IRecommendationItem;
    }

    // Recursively search in children
    if (item.children && item.children.length > 0) {
      for (const child of item.children) {
        const found = findParameterById(child, targetId);
        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  // Helper function to check if any selected parameters are invalid
  const someSelectedRecommendationsAreIncomplete = useMemo(() => {
    if (!recommendationsTable || selectedParameterIds.length === 0) return false;

    // Collect all selected parameters by recursively searching the hierarchy
    const selectedParameters: IRecommendationItem[] = [];
    const foundIds = new Set<number>();

    // Search for each selected parameter ID in all file rows
    selectedParameterIds.forEach((paramId) => {
      // Skip if already found
      if (foundIds.has(paramId)) return;

      // Search in all file rows
      for (const fileRow of recommendationsTable) {
        const parameter = findParameterById(fileRow, paramId);
        if (parameter) {
          selectedParameters.push(parameter);
          foundIds.add(paramId);
          break; // Found, no need to search in other file rows
        }
      }
    });

    // Check if any selected parameters are invalid
    return selectedParameters.some((param) => !isRecommendationFilled(param));
  }, [recommendationsTable, selectedParameterIds]);

  const onMultiChangeRecommendationStatus = useCallback(async () => {
    if (!recommendationsTable) return;
    try {
      const data = await updateRecommendationStatusAction({
        accepted_ids: selectedParameterIds.map((id) => id),
        declined_ids: [],
        new_ids: []
      });
      if (!data) return;

      const updatedTable = updateRecommendationsTable(data);

      setRecommendationsTable(updatedTable);
      clearAllSelections();
    } catch (err) {
      errorModal({ msg: Object.values((err as IApiServiceError).errors)[0][0] || "Something went wrong" });
    }
  }, [
    updateRecommendationStatusAction,
    selectedParameterIds,
    recommendationsTable,
    updateRecommendationsTable,
    errorModal
  ]);

  // Helper function to collect all IRecommendationItem elements recursively
  const collectRecommendationItems = (item: IRecommendationChild): IRecommendationItem[] => {
    const items: IRecommendationItem[] = [];

    // If current item has children, recursively collect from them
    if (item.children && item.children.length) {
      item.children.forEach((child) => {
        items.push(...collectRecommendationItems(child));
      });
    } else {
      // If no children (or empty array), add it for validation
      items.push(item as IRecommendationItem);
    }

    return items;
  };

  // Helper function to check if a recommendation with children is filled
  const isRecommendationWithChildrenFilled = (recommendation: IRecommendationChild): boolean => {
    const allRecommendationItems = collectRecommendationItems(recommendation);
    // Check if all collected IRecommendationItem elements are filled
    return allRecommendationItems.every((item) => isRecommendationFilled(item));
  };

  // ------------ filters  ---------

  const updateAiDataFilters = useCallback(
    (params: Partial<AiDataFilters> | null) => {
      if (isNull(params)) {
        removeParams(["createdAt", "dealId"]);
        return setFilters(defaultFileFilters);
      }
      removeParams(["createdAt", "dealId"]);
      setFilters({ ...filters, ...params });
    },
    [filters, removeParams]
  );

  // ------------ expand  ---------

  const toggleRowExpansion = (uniqueId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(uniqueId)) {
        newSet.delete(uniqueId);
      } else {
        newSet.add(uniqueId);
      }
      return newSet;
    });
  };

  // ------------ checkbox selection  ---------

  // Helper to get only SELECTABLE descendant unique IDs
  // Recursively collects all parameters (items without children) with status "new"
  // and with status "new" (not accepted or declined)
  const getSelectableDescendantIds = (item: IRecommendationChild | IRecommendationData, fileId: number): string[] => {
    const ids: string[] = [];

    if (item.children && item.children.length > 0) {
      // Recursively check all children
      item.children.forEach((child) => {
        // If child has NO children, it's a parameter and selectable
        const isParameter = !child.children || child.children.length === 0;
        const hasNewStatus = "status" in child && child.status === RecommendationStatus.new;

        if (isParameter && hasNewStatus) {
          // It's a parameter - add it
          const uniqueId = generateUniqueId(child, fileId);
          ids.push(uniqueId);
        } else if (child.children && child.children.length > 0) {
          // It's a group - recursively get all selectable descendants
          const nestedIds = getSelectableDescendantIds(child, fileId);
          ids.push(...nestedIds);
        }
      });
    }

    return ids;
  };

  // Helper to get all parameter IDs (actual IRecommendationItem with id field)
  // Recursively collects all parameter IDs from nested groups
  // and with status "new" (not accepted or declined)
  const getAllParameterIds = (item: IRecommendationChild | IRecommendationData): number[] => {
    const ids: number[] = [];

    if (item.children && item.children.length > 0) {
      // Recursively check all children
      item.children.forEach((child) => {
        // If child has NO children and has an ID and status "new", it's a parameter
        const isParameter = !child.children || child.children.length === 0;
        const hasNewStatus = "status" in child && child.status === RecommendationStatus.new;
        const hasId = "id" in child && child.id;

        if (isParameter && hasNewStatus && hasId) {
          ids.push(child.id);
        } else if (child.children && child.children.length > 0) {
          // It's a group - recursively get all parameter IDs
          const nestedIds = getAllParameterIds(child);
          ids.push(...nestedIds);
        }
      });
    } else {
      // If item itself has no children (it's a parameter), return its ID
      const hasNewStatus = "status" in item && item.status === RecommendationStatus.new;
      const hasId = "id" in item && item.id;

      if (hasNewStatus && hasId) {
        ids.push(item.id);
      }
    }

    return ids;
  };

  // Check if uniqueId is selected
  const isUniqueIdSelected = (
    uniqueId: string,
    item?: IRecommendationChild | IRecommendationData,
    fileId?: number
  ): boolean => {
    // For FILE rows (IRecommendationData), NEVER return true - they can only be indeterminate
    if (item && "file" in item && "deal" in item) {
      return false;
    }

    // For items with children (groups), check if ALL selectable descendants are selected
    if (item && fileId && "children" in item && item.children && item.children.length > 0) {
      const selectableIds = getSelectableDescendantIds(item as IRecommendationChild, fileId);
      if (selectableIds.length === 0) return false;
      return selectableIds.every((id) => selectedUiIds.has(id));
    }

    // For parameter items, check if the item itself is selected
    return selectedUiIds.has(uniqueId);
  };

  // Check if uniqueId is partially selected (some but not all selectable children are selected)
  const isUniqueIdPartiallySelected = (item?: IRecommendationChild | IRecommendationData, fileId?: number): boolean => {
    if (!item || !fileId) return false;

    // Only items with children can be partially selected
    if (!("children" in item) || !item.children || item.children.length === 0) return false;

    // Get all selectable descendants
    const selectableIds = getSelectableDescendantIds(item as IRecommendationChild, fileId);

    if (selectableIds.length === 0) return false;

    // Count how many are selected
    const selectedCount = selectableIds.filter((id) => selectedUiIds.has(id)).length;

    // For FILE rows (IRecommendationData), show indeterminate if ANY are selected
    if ("file" in item && "deal" in item) {
      return selectedCount > 0;
    }

    // For other items with children, partially selected if some (but not all) are selected
    return selectedCount > 0 && selectedCount < selectableIds.length;
  };

  // Handle hierarchical checkbox change
  const handleHierarchicalCheckboxChange = useCallback(
    (uniqueId: string, checked: boolean, item?: IRecommendationChild | IRecommendationData, fileId?: number) => {
      // Determine if this is a file row (outside setState to use in both places)
      const isFileRow = item && "file" in item && "deal" in item;
      // Determine if this is a group (has children)
      const isGroup = item && "children" in item && item.children && item.children.length > 0;

      setSelectedUiIds((prev) => {
        const newSet = new Set(prev);

        if (isFileRow && item && fileId) {
          const selectableIds = getSelectableDescendantIds(item, fileId);
          const selectedCount = selectableIds.filter((id) => prev.has(id)).length;

          // If any are selected (indeterminate state), deselect all
          if (selectedCount > 0) {
            selectableIds.forEach((id) => newSet.delete(id));
          } else {
            // If none are selected, select all
            selectableIds.forEach((id) => newSet.add(id));
          }

          return newSet;
        }

        // For groups (with children), use toggle logic like files
        if (isGroup && item && fileId) {
          const selectableIds = getSelectableDescendantIds(item, fileId);
          const selectedCount = selectableIds.filter((id) => prev.has(id)).length;

          // If any are selected (indeterminate or fully selected), deselect all
          if (selectedCount > 0) {
            selectableIds.forEach((id) => newSet.delete(id));
          } else {
            // If none are selected, select all
            selectableIds.forEach((id) => newSet.add(id));
          }

          return newSet;
        }

        // Regular logic for parameters (no children)
        if (checked) {
          // Parameter - select it
          newSet.add(uniqueId);
        } else {
          // Deselect parameter
          newSet.delete(uniqueId);
        }

        return newSet;
      });

      // Update parameter IDs for actual recommendation items
      if (item) {
        const parameterIds = getAllParameterIds(item);

        setSelectedParameterIds((prev) => {
          // Special handling for file rows and groups - toggle behavior
          if (isFileRow || isGroup) {
            const anyCurrentlySelected = parameterIds.some((id) => prev.includes(id));

            if (anyCurrentlySelected) {
              // Deselecting - remove all parameter IDs
              return prev.filter((id) => !parameterIds.includes(id));
            } else {
              // Selecting - add all parameter IDs
              return Array.from(new Set([...prev, ...parameterIds]));
            }
          }

          // Regular logic for parameters (no children)
          if (checked) {
            return Array.from(new Set([...prev, ...parameterIds]));
          } else {
            return prev.filter((id) => !parameterIds.includes(id));
          }
        });
      }
    },
    []
  );

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedUiIds(new Set());
    setSelectedParameterIds([]);
  };

  // ------------ build filter payload helper ------------

  const getTablePayload = (pageNum: number, perPage: number) => {
    if (createdAt || dealId) {
      return {
        ...(dealId ? { deal_ids: [Number(dealId)] } : {}),
        ...(createdAt ? { created_at: { from: createdAt, to: createdAt } } : {}),
        page: pageNum,
        per_page: perPage
      };
    }

    return {
      ...(filters.deals?.length ? { deal_ids: filters.deals?.map((deal) => deal.id) } : {}),
      ...(filters.investors?.length ? { investor_ids: filters.investors?.map((investor) => investor.id) } : {}),
      ...(filters.measures?.length ? { measure_ids: filters.measures?.map((measure) => measure.id) } : {}),
      ...(filters.effectiveDate
        ? { effective_date: { from: filters.effectiveDate.from!, to: filters.effectiveDate.to! } }
        : {}),
      ...(filters.createdAt ? { created_at: { from: filters.createdAt.from!, to: filters.createdAt.to! } } : {}),
      ...(filters.status?.length ? { statuses: filters.status?.map((status) => status.toLowerCase()) } : {}),
      page: pageNum,
      per_page: perPage
    };
  };

  // ------------ pagination handlers ------------

  const handleChangePage = useCallback(
    (newPage: number) => {
      clearAllSelections();
      const filterPayload = getTablePayload(newPage, pagination?.per_page || defaultPerPage);
      getTable(filterPayload);
    },
    [createdAt, dealId, filters, pagination, getTable]
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      clearAllSelections();
      const newRowsPerPage = parseInt(event.target.value, 10);
      const filterPayload = getTablePayload(1, newRowsPerPage);
      getTable(filterPayload);
    },
    [createdAt, dealId, filters, getTable]
  );

  // ------------ useEffect ------------

  useEffect(() => {
    const filterPayload = getTablePayload(1, pagination?.per_page || defaultPerPage);
    getTable(filterPayload);
  }, [createdAt, dealId, filters]);

  return {
    // Opened cell for editing
    openedCell,
    setOpenedCell,
    handleCellClick,
    userHasEditPermission,

    // Recommendations table data
    recommendationsTable,
    isRecommendationsTableLoading,
    getTable,
    setRecommendationsTable,
    filters,
    updateAiDataFilters,

    // Expand rows
    expandedRows,
    toggleRowExpansion,

    // Update recommendation status and approval
    onChangeRecommendationStatus,
    onMultiChangeRecommendationStatus,
    onGroupRowApprove,
    isRecommendationFilled,
    getMissingRequiredFields,
    isRecommendationWithChildrenFilled,
    onChangeSpecificRecommendationValue,
    regenerateRecommendationData,
    allChildAccepted,

    // Generate unique ID for rows
    generateUniqueId,

    // Checkbox selection for hierarchical checkbox
    selectedUiIds,
    selectedParameterIds,
    someSelectedRecommendationsAreIncomplete,
    handleHierarchicalCheckboxChange,
    isUniqueIdSelected,
    isUniqueIdPartiallySelected,
    clearAllSelections,

    // Pagination for recommendations table
    pagination,
    handleChangePage,
    handleChangeRowsPerPage
  };
};
